import styles from './Startseite.module.scss';
import { useSelector, useDispatch } from 'react-redux';
import { NavigationEntry, NavigationGroup } from '../../store/Navigation.store';
import { Link } from 'react-router-dom';
import type { RootState } from '../../configureStore';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle';
import {
    BestelluebersichtSelectors,
    acknowledgeAuftragskorb,
    fetchAuftragskorbHasNew,
    setAuftragskorbClicked,
} from '../../store/Bestelluebersicht.store';
import { routes } from '../../shared/constants';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import FeatureToggle from '../../shared/ui-components/FeatureToggle/FeatureToggle';
import { PostHogFeature } from 'posthog-js/react';
import { PARTNERPORTAL_NAME } from '../../shared/constants/company';

const Startseite = (): JSX.Element => {
    const menuEntries: NavigationGroup[] = useSelector((rootState: RootState): NavigationGroup[] => rootState.navigation.navigationGroups);
    const hasNewAuftraege = useSelector((state: RootState) => BestelluebersichtSelectors.hasNewAuftraege(state));
    const dispatch = useDispatch();
    useDocumentTitle(PARTNERPORTAL_NAME, { disablePrefix: true });

    useEffect(() => {
        dispatch({ type: fetchAuftragskorbHasNew });
    }, [dispatch]);
    return (
        <div className={styles._page}>
            <h2>DEU·PA</h2>
            <p>{`Willkommen im ${PARTNERPORTAL_NAME}`}</p>
            <FeatureToggle featureName="MAINTENANCE_NOTICE">
                <div className={styles._maintenanceNotice}>
                    <div className={styles._maintenanceIcon} aria-hidden="true">
                        <svg width="38" height="38" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <g fill="#404040">
                                <circle cx="4" cy="12" r="1.6" />
                                <circle cx="9" cy="12" r="1.6" />
                                <circle cx="14" cy="12" r="1.6" />
                                <circle cx="4" cy="17" r="1.6" />
                                <circle cx="9" cy="17" r="1.6" />
                                <circle cx="14" cy="17" r="1.6" />
                                <circle cx="4" cy="22" r="1.6" />
                                <circle cx="9" cy="22" r="1.6" />
                                <circle cx="14" cy="22" r="1.6" />
                            </g>
                            <g>
                                <circle cx="18" cy="8" r="5.5" fill="#404040" />
                                <text x="18" y="11" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#fff">
                                    !
                                </text>
                            </g>
                        </svg>
                    </div>
                    <div className={styles._maintenanceText}>
                        <p>Liebe Nutzerinnen und Nutzer des Partnerportals,</p>
                        <p>
                            zwischen dem <strong>18. und 25. September</strong> aktualisieren wir unser Warenwirtschaftssystem. Während
                            dieser Zeit kann es systemseitig zu einigen Einschränkungen kommen. Bitte beachten Sie daher unser
                            Informationsschreiben{' '}
                            <a href="http://www.deuka.de/Infoseite-fuer-DEU-PA" target="_blank" rel="noopener noreferrer">
                                hier
                            </a>
                            .
                        </p>
                        <p>Vielen Dank für Ihr Verständnis.</p>
                    </div>
                </div>
            </FeatureToggle>
            <PostHogFeature flag="systemhinweis-DEUPA-726" match={true}>
                <div className={styles._maintenanceNotice}>
                    <div className={styles._maintenanceText}>
                        <p>
                            <strong>Sie füttern Schaf-, Kalb- oder Bullenfutter?</strong>
                        </p>
                        <p>
                            Dann beachten Sie bitte, dass wir unsere Produkte neu gruppiert und einige Produktbezeichnungen angepasst haben.
                            Artikelnummern, Inhaltsstoffe und Gebindegrößen bleiben unverändert.
                        </p>
                        <p>
                            Alle Änderungen im Überblick finden Sie{' '}
                            <a
                                href="https://www.deuka.de/neue-produktbezeichnungen-bulle-kalb-schaf/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                hier
                            </a>
                            .
                        </p>
                    </div>
                </div>
            </PostHogFeature>
            <div className={styles._navigationContainer}>
                {menuEntries.map((group, groupIndex) => {
                    if (!group.entries || group.entries.length === 0) {
                        return null;
                    }

                    return <GroupWithCheck key={groupIndex} group={group} dispatch={dispatch} hasNewAuftraege={hasNewAuftraege} />;
                })}
            </div>
        </div>
    );
};

const GroupWithCheck = ({ group, dispatch, hasNewAuftraege }: { group: NavigationGroup; dispatch: any; hasNewAuftraege: boolean }) => {
    const navigationGroupRef = useRef<HTMLDivElement>(null);
    const [hasVisibleChildren, setHasVisibleChildren] = useState(true);

    const checkChildren = () => {
        if (navigationGroupRef.current) {
            const children = navigationGroupRef.current.children;
            setHasVisibleChildren(children.length > 0);
        }
    };

    useLayoutEffect(() => {
        checkChildren();
    });

    useEffect(() => {
        if (!navigationGroupRef.current) return;

        const observer = new MutationObserver(() => {
            checkChildren();
        });

        observer.observe(navigationGroupRef.current, {
            childList: true,
            subtree: true,
        });

        const timer = setTimeout(checkChildren, 100);

        return () => {
            observer.disconnect();
            clearTimeout(timer);
        };
    }, [group.entries]);

    if (!hasVisibleChildren) {
        return null;
    }

    return (
        <div className={styles._group}>
            <h4>{group.groupLabel}</h4>
            <div className={styles._navigationGroup} ref={navigationGroupRef}>
                {group.entries.map((entry: NavigationEntry, entryIndex) => {
                    if (entry.isExternalLink) {
                        return (
                            <a
                                data-cy={`navigation-cards${entry.name}`}
                                key={entryIndex}
                                href={entry.route}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles._navigationEntry}
                            >
                                {entry.image && <img className={styles._navigationImage} src={entry.image} alt=" " />}
                                <div className={styles._textWrapper}>
                                    <span className={styles._navigationText}>{entry.label}</span>
                                </div>
                            </a>
                        );
                    }

                    return (
                        <div key={entryIndex} style={{ position: 'relative' }}>
                            <div
                                onClick={() => {
                                    if (entry.name === routes.auftragsuebersicht) {
                                        dispatch(setAuftragskorbClicked({ clicked: true }));
                                        dispatch({ type: acknowledgeAuftragskorb });
                                    }
                                }}
                            >
                                <Link data-cy={`navigation-cards${entry.name}`} to={entry.route} className={styles._navigationEntry}>
                                    {entry.image && <img className={styles._navigationImage} src={entry.image} alt=" " />}
                                    <div className={styles._textWrapper}>
                                        <span className={styles._navigationText}>{entry.label}</span>
                                    </div>
                                </Link>
                            </div>
                            {hasNewAuftraege && entry.name === routes.auftragsuebersicht && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '4px',
                                        right: '12px',
                                        width: '10px',
                                        height: '10px',
                                        backgroundColor: '#ff0000',
                                        borderRadius: '50%',
                                    }}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
            <br />
        </div>
    );
};

export default Startseite;
